import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ShoppingCart, Utensils } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "../components/NotificationBell";
import { LayoutDashboard, FileText, TrendingUp, Users, Euro, Bell, LogOut, Leaf, Package, Building2, Users2, CreditCard, Layers, Settings as SettingsIcon, Boxes, FileSpreadsheet, Percent, Landmark, Truck } from "lucide-react";

const nav = [
  { to: "/", label: "Panoramica", icon: LayoutDashboard, end: true, id: "overview" },
  { to: "/acquisti", label: "Acquisti", icon: FileText, id: "invoices" },
  { to: "/andamento", label: "Andamento prezzi", icon: TrendingUp, id: "trends" },
  { to: "/fornitori", label: "Confronto fornitori", icon: Users, id: "suppliers" },
  { to: "/anagrafica-fornitori", label: "Anagrafica Fornitori", icon: Truck, id: "suppliers-registry" },
  { to: "/prodotti", label: "Prodotti & Reparti", icon: Package, id: "products" },
  { to: "/anagrafica", label: "Anagrafica Prodotti", icon: Boxes, id: "anagrafica" },
  { to: "/vendite", label: "Vendite", icon: Euro, id: "sales" },
  { to: "/import-vendite", label: "Import Vendite", icon: FileSpreadsheet, id: "sales-import" },
  { to: "/marginalita", label: "Marginalità", icon: Percent, id: "margins" },
  { to: "/avvisi", label: "Avvisi prezzi", icon: Bell, id: "alerts" },
];

export default function Layout() {
  const { user, logout, impersonating, stopImpersonate } = useAuth();
  const navigate = useNavigate();
  const trialDays = user?.plan === "trial" && user?.plan_expires_at ? Math.max(0, Math.ceil((new Date(user.plan_expires_at) - Date.now()) / 86400000)) : null;

  const doLogout = async () => { await logout(); navigate("/login"); };
  const exitImpersonation = async () => { await stopImpersonate(); navigate("/admin"); };

  return (
    <div className="min-h-screen flex bg-[#FAFAF9]">
      <aside className="w-64 shrink-0 bg-[#F5F5F4] border-r border-stone-200 flex flex-col fixed h-screen noise-overlay">
        <div className="px-6 py-7 flex items-center gap-2">
          <div className="w-9 h-9 rounded-sm bg-brand flex items-center justify-center">
            <Leaf className="text-white" size={18} strokeWidth={1.5} />
          </div>
          <span className="font-serif text-xl text-stone-900 tracking-tight">Q<span className="text-brand">COST</span></span>
        </div>
        {trialDays != null && (
          <div data-testid="trial-badge" className="mx-6 -mt-3 mb-1 text-xs font-semibold text-brand bg-brand/10 rounded-full px-3 py-1 inline-flex items-center gap-1.5 self-start">
            <Leaf size={12} /> Prova: {trialDays} {trialDays === 1 ? "giorno" : "giorni"}
          </div>
        )}

        <nav className="flex-1 px-3 space-y-1 mt-4">
          {[...nav, ...(user?.acquisti_enabled ? [{ to: "/procurement", label: "Ordini fornitori", icon: ShoppingCart, id: "procurement" }] : []), ...(user?.ricette_enabled ? [{ to: "/ricette", label: "Ricette", icon: Utensils, id: "recipes" }] : [])].map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-200/60"
                }`}>
              <n.icon size={18} strokeWidth={1.5} />
              {n.label}
            </NavLink>
          ))}
          {user?.role === "owner" && user?.platform_role !== "super_admin" && (
            <NavLink to="/azienda" data-testid="nav-company-profile" className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-200/60"}`}>
              <Landmark size={18} strokeWidth={1.5} /> Anagrafica azienda
            </NavLink>
          )}
          {user?.role === "owner" && user?.platform_role !== "super_admin" && (
            <NavLink to="/team" data-testid="nav-team" className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-200/60"}`}>
              <Users2 size={18} strokeWidth={1.5} /> Team
            </NavLink>
          )}
          {user?.role === "owner" && user?.platform_role !== "super_admin" && (
            <NavLink to="/billing" data-testid="nav-billing" className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-200/60"}`}>
              <CreditCard size={18} strokeWidth={1.5} /> Abbonamento
            </NavLink>
          )}
          {user?.platform_role === "super_admin" && (
            <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-200/60"}`}>
              <Building2 size={18} strokeWidth={1.5} /> Aziende
            </NavLink>
          )}
          {user?.platform_role === "super_admin" && (
            <NavLink to="/piani" data-testid="nav-plans" className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-200/60"}`}>
              <Layers size={18} strokeWidth={1.5} /> Piani
            </NavLink>
          )}
          {user?.platform_role === "super_admin" && (
            <NavLink to="/impostazioni" data-testid="nav-settings" className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive ? "bg-brand text-white" : "text-stone-600 hover:bg-stone-200/60"}`}>
              <SettingsIcon size={18} strokeWidth={1.5} /> Impostazioni
            </NavLink>
          )}
        </nav>

        <div className="p-4 border-t border-stone-200">
          <div className="px-2 mb-3">
            <p className="text-sm font-semibold text-stone-800 truncate">{user?.name}</p>
            <p className="text-xs text-stone-500 truncate">{user?.email}</p>
          </div>
          <button data-testid="logout-btn" onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-600 hover:text-alert transition-colors rounded-sm hover:bg-stone-200/60">
            <LogOut size={16} strokeWidth={1.5} /> Esci
          </button>
          <div className="mt-3 px-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-stone-400">
            <a data-testid="legal-privacy" href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-brand transition-colors">Privacy</a>
            <span className="text-stone-300">·</span>
            <a data-testid="legal-cookie" href="/cookie-policy" target="_blank" rel="noopener noreferrer" className="hover:text-brand transition-colors">Cookie</a>
            <span className="text-stone-300">·</span>
            <a data-testid="legal-terms" href="/termini" target="_blank" rel="noopener noreferrer" className="hover:text-brand transition-colors">Termini</a>
          </div>
        </div>
      </aside>

      <main className="flex-1 ml-64 p-8 lg:p-10">
        {user?.platform_role !== "super_admin" && (
          <div className="flex justify-end mb-4 -mt-2">
            <NotificationBell />
          </div>
        )}
        {impersonating && (
          <div data-testid="impersonation-banner" className="mb-6 -mt-2 flex items-center justify-between gap-3 rounded-sm border border-brand-accent/40 bg-brand-accent/10 px-4 py-3">
            <p className="text-sm text-stone-700">Stai operando come <b>{impersonating}</b> (accesso super-admin).</p>
            <button data-testid="exit-impersonation-btn" onClick={exitImpersonation}
              className="shrink-0 rounded-full bg-brand text-white px-4 py-1.5 text-xs font-semibold hover:bg-brand-hover transition-colors">
              Torna al pannello admin
            </button>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
